/**
 * SaveRouting — the routing re-check notice, the detected-work
 * gate with its log-only choice, the Action/Decision toggle, and for a decision
 * the raise/resolve choice with the resolve picker.
 */

import { ActionDecisionToggle, type ActionDecisionValue } from "./ActionDecisionToggle";
import { ResolveDecisionPicker } from "./ResolveDecisionPicker";
import type { Decision } from "../slice/decision-slice";
import type { DecisionMode } from "../triage/save-screen-types";

export interface SaveRoutingProps {
  routingReviewPending: boolean;
  activeRoutingDetection: boolean;
  logOnly: boolean;
  handleLogOnlyChoice: () => void;
  toggle: ActionDecisionValue | null;
  handleToggleChange: (value: ActionDecisionValue) => void;
  decisionMode: DecisionMode | null;
  setDecisionMode: (mode: DecisionMode) => void;
  openDecisions: Decision[];
  selectedDecisionRef: string | null;
  setSelectedDecisionRef: (decision_ref: string) => void;
  rationale: string;
  setRationale: (text: string) => void;
}

export function SaveRouting({
  routingReviewPending,
  activeRoutingDetection,
  logOnly,
  handleLogOnlyChoice,
  toggle,
  handleToggleChange,
  decisionMode,
  setDecisionMode,
  openDecisions,
  selectedDecisionRef,
  setSelectedDecisionRef,
  rationale,
  setRationale,
}: SaveRoutingProps) {
  return (
    <>
      {routingReviewPending && (
        <section data-testid="save-screen-routing-rechecking" role="status" aria-live="polite">
          <strong>Checking this edited note for work…</strong> Save will be available after Mark checks the exact text.
        </section>
      )}

      {activeRoutingDetection && !routingReviewPending && (
        <section data-testid="save-screen-routing-gate" aria-labelledby="save-screen-routing-heading">
          <p id="save-screen-routing-heading" role="status" aria-live="polite"><strong>Mark detected work in this note.</strong> Detection is not a decision. Choose where it belongs before saving.</p>
          <button
            type="button"
            data-testid="save-screen-routing-log-only"
            aria-pressed={logOnly}
            onClick={handleLogOnlyChoice}
          >
            Keep as Daily Log only
          </button>
        </section>
      )}

      <ActionDecisionToggle value={toggle} onChange={handleToggleChange} />

      {toggle === "decision" && (
        <section data-testid="save-screen-decision-mode">
          <button
            type="button"
            data-testid="save-screen-decision-mode-raise"
            aria-pressed={decisionMode === "raise"}
            onClick={() => setDecisionMode("raise")}
          >
            New decision
          </button>
          <button
            type="button"
            data-testid="save-screen-decision-mode-resolve"
            aria-pressed={decisionMode === "resolve"}
            onClick={() => setDecisionMode("resolve")}
          >
            Resolve existing
          </button>
          {decisionMode === "resolve" && (
            <ResolveDecisionPicker
              decisions={openDecisions}
              selectedId={selectedDecisionRef}
              onSelect={setSelectedDecisionRef}
              rationale={rationale}
              onRationaleChange={setRationale}
            />
          )}
        </section>
      )}
    </>
  );
}
